import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { orderService } from '../../services/orderService';
import OrderFilterForm from '../../components/orders/OrderFilterForm';
import OrderStatusBadge from '../../components/orders/OrderStatusBadge';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import { Search, Filter, Eye, UserPlus } from 'lucide-react';

const OrdersPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [totalOrders, setTotalOrders] = useState(0);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10
    });
    const [filters, setFilters] = useState({
        status: '',
        fleet_id: user.user_type === 'admin' ? '' : user.fleet_id,
        driver_id: '',
        passenger_id: '',
        start_date: '',
        end_date: '',
        search: ''
    });

    useEffect(() => {
        fetchOrders();
    }, [pagination.currentPage, filters]);

    const fetchOrders = async () => {
        setIsLoading(true);
        try {
            const { data, meta } = await orderService.getOrders({
                page: pagination.currentPage,
                limit: pagination.perPage,
                ...filters
            });

            setOrders(data);
            setTotalOrders(meta.total);
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleFilterSubmit = (newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        setPagination(prev => ({ ...prev, currentPage: 1 })); // 重置到第一頁
        setShowFilters(false);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleViewOrder = (orderId) => {
        navigate(`/orders/${orderId}`);
    };

    const columns = [
        {
            header: '訂單編號',
            accessor: 'order_number',
            cell: (row) => (
                <span className="font-medium">{row.order_number}</span>
            )
        },
        {
            header: '乘客',
            accessor: 'passenger_name',
            cell: (row) => (
                <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-200 rounded-full mr-2"></div>
                    <span>{row.passenger_name}</span>
                </div>
            )
        },
        {
            header: '司機',
            accessor: 'driver_name',
            cell: (row) => (
                row.driver_name ? (
                    <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-200 rounded-full mr-2"></div>
                        <span>{row.driver_name}</span>
                    </div>
                ) : (
                    <span className="text-gray-400">未指派</span>
                )
            )
        },
        {
            header: '車資',
            accessor: 'fare',
            cell: (row) => (
                <span className="font-medium">${row.actual_fare || row.estimated_fare}</span>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <OrderStatusBadge status={row.status} />
            )
        },
        {
            header: '建立時間',
            accessor: 'created_at',
            cell: (row) => (
                <span className="text-gray-600">
                    {new Date(row.created_at).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </span>
            )
        },
        {
            header: '操作',
            accessor: 'actions',
            cell: (row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleViewOrder(row.order_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="查看詳情"
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">訂單管理</h1>
                <button
                    onClick={() => navigate('/orders/new')}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <UserPlus className="w-5 h-5 mr-2" />
                    新建訂單
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                        <div className="flex-1 max-w-md relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                placeholder="搜尋訂單..."
                                value={filters.search}
                                onChange={handleSearch}
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-4 px-3 py-2 border border-gray-300 rounded-md flex items-center hover:bg-gray-50"
                        >
                            <Filter className="w-5 h-5 mr-1" />
                            篩選
                        </button>
                    </div>

                    {showFilters && (
                        <div className="mt-4">
                            <OrderFilterForm
                                initialValues={filters}
                                onSubmit={handleFilterSubmit}
                                onCancel={() => setShowFilters(false)}
                                isAdmin={user.user_type === 'admin'}
                            />
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <DataTable
                            columns={columns}
                            data={orders}
                            emptyMessage="無符合條件的訂單"
                        />
                        <div className="p-4 border-t border-gray-200">
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalItems={totalOrders}
                                perPage={pagination.perPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default OrdersPage;